import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import ReactDom from "react-dom";
import "components/modal/Modal.scss";
import { FormattedMessage, useIntl } from "react-intl";
import Button from "core/buttons/button/Button";
import Icon from "core/icon/Icon";
import InputSearchTag from "core/inputs/input-search/InputSearchTag";
import { Typography } from "core/typography/Typography";
import ValidationModal from "components/modal/ValidationModal";
import {
  DELIVERY_NOTE_SHARE_LIMITS,
  QUERY_STATUS,
  TAG_TYPES,
} from "constants/constants";
import {
  DELIVERY_NOTE_SHARE_ACTIONS,
  DELIVERY_NOTE_SHARE_MESSAGES,
} from "constants/forms";

const COLLEAGUE_SEARCH_MIN_LENGTH = 2;

const DeliveryNoteTemplateShareModal = ({
  template,
  onSearchColleagues,
  onShare,
  onClose,
}) => {
  const intl = useIntl();
  const [colleagueOptions, setColleagueOptions] = useState([]);
  const [selectedColleagues, setSelectedColleagues] = useState([]);
  const [message, setMessage] = useState("");
  const [emptySelectionError, setEmptySelectionError] = useState(false);
  const [shareStatus, setShareStatus] = useState(QUERY_STATUS.INITIAL);
  const [shareError, setShareError] = useState(null);
  const [sharedNames, setSharedNames] = useState("");
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (!template || shareStatus !== QUERY_STATUS.INITIAL) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [template, shareStatus, onClose]);

  if (!template) return null;

  const unknownColleague = intl.formatMessage(
    DELIVERY_NOTE_SHARE_MESSAGES.unknownColleague
  );
  const alreadySharedWith = (template.shared_with_names || []).map(
    (name) => name || unknownColleague
  );

  const closeValidation = () => {
    const wasSuccess = shareStatus === QUERY_STATUS.SUCCESS;
    setShareStatus(QUERY_STATUS.INITIAL);
    setShareError(null);
    if (wasSuccess) onClose();
  };

  const submit = async () => {
    if (isSubmittingRef.current) return;
    if (!selectedColleagues.length) {
      setEmptySelectionError(true);
      return;
    }
    isSubmittingRef.current = true;
    setEmptySelectionError(false);
    setShareStatus(QUERY_STATUS.LOADING);
    try {
      const names = selectedColleagues.map((colleague) => colleague.label);
      const { sharedWith, fieldErrors } = await onShare({
        id: template.id,
        colleagues: selectedColleagues,
        message,
      });
      if (sharedWith) {
        setSharedNames(names.join(", "));
        setShareStatus(QUERY_STATUS.SUCCESS);
        return;
      }
      setShareError(
        fieldErrors?.colleague_ids || fieldErrors?.message || null
      );
      setShareStatus(QUERY_STATUS.ERROR);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const errorMessages = () => {
    const translationKey = shareError?.translationKey;
    if (translationKey && intl.messages[translationKey]) {
      return {
        errorMessageId: translationKey,
        errorMessageDefault: shareError.message,
      };
    }
    return {
      errorMessageId: DELIVERY_NOTE_SHARE_MESSAGES.errorMessage.id,
      errorMessageDefault:
        shareError?.message ||
        DELIVERY_NOTE_SHARE_MESSAGES.errorMessage.defaultMessage,
    };
  };

  return ReactDom.createPortal(
    <>
      <div className="overlay-style" onClick={onClose} />
      <div
        className="modal-style delivery-note-share-modal"
        id="delivery-note-template-share-modal"
      >
        <div className="delivery-note-share-modal__header">
          <div>
            <Typography
              className="title-3 violet text-uppercase"
              type="h2"
              label={
                <FormattedMessage
                  {...DELIVERY_NOTE_SHARE_MESSAGES.title}
                  values={{ name: template.name }}
                />
              }
            />
            <Typography
              className="text-3 violet"
              type="p"
              label={
                <FormattedMessage
                  {...DELIVERY_NOTE_SHARE_MESSAGES.subtitle}
                  values={{
                    issuer: template.issuer?.company_name || "—",
                    recipient: template.recipient?.company_name || "—",
                  }}
                />
              }
            />
          </div>
          <Icon
            icon="close"
            color="violet"
            onClick={onClose}
            dataTestId="delivery-note-template-share-close"
          />
        </div>

        <div className="delivery-note-share-modal__body">
          {alreadySharedWith.length > 0 && (
            <div className="delivery-note-share-modal__history">
              <Typography
                className="text-3 violet text-uppercase"
                type="span"
                label={
                  <FormattedMessage
                    {...DELIVERY_NOTE_SHARE_MESSAGES.alreadySharedWith}
                  />
                }
              />
              <Typography
                className="text-3 deep-purple"
                type="p"
                label={alreadySharedWith.join(", ")}
              />
            </div>
          )}

          <label
            className="label sourcesanspro-normal-shadow-blue-14px label-standard"
            htmlFor="input-search-tag-delivery-note-share-colleagues"
          >
            <FormattedMessage
              {...DELIVERY_NOTE_SHARE_MESSAGES.colleaguesLabel}
            />
          </label>
          <div className="delivery-note-share-modal__colleagues">
            <InputSearchTag
              id="delivery-note-share-colleagues"
              dataTestId="delivery-note-share-colleagues"
              placeholder={DELIVERY_NOTE_SHARE_MESSAGES.colleaguesPlaceholder}
              popup={{
                title: DELIVERY_NOTE_SHARE_MESSAGES.colleaguesPopupTitle,
              }}
              type={TAG_TYPES.PEOPLE}
              minLength={COLLEAGUE_SEARCH_MIN_LENGTH}
              limitChoice={DELIVERY_NOTE_SHARE_LIMITS.maxColleagues}
              options={colleagueOptions}
              selectedOptions={selectedColleagues}
              getOptions={async (search) =>
                setColleagueOptions(await onSearchColleagues(search))
              }
              setValue={(selected) => {
                setSelectedColleagues(
                  selected.slice(0, DELIVERY_NOTE_SHARE_LIMITS.maxColleagues)
                );
                if (selected.length) setEmptySelectionError(false);
              }}
            />
          </div>

          {emptySelectionError && (
            <Typography
              className="text-3 alizarin-crimson"
              type="p"
              label={
                <FormattedMessage
                  {...DELIVERY_NOTE_SHARE_MESSAGES.noColleagueSelected}
                />
              }
            />
          )}

          <label
            className="label sourcesanspro-normal-shadow-blue-14px label-standard"
            htmlFor="delivery-note-share-message"
          >
            <FormattedMessage {...DELIVERY_NOTE_SHARE_MESSAGES.messageLabel} />
          </label>
          <textarea
            id="delivery-note-share-message"
            className="delivery-note-share-modal__message"
            value={message}
            maxLength={DELIVERY_NOTE_SHARE_LIMITS.maxMessage}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={intl.formatMessage(
              DELIVERY_NOTE_SHARE_MESSAGES.messagePlaceholder
            )}
            data-testid="delivery-note-share-message"
          />
        </div>

        <div className="delivery-note-share-modal__buttons">
          <Typography
            className="text-3 violet"
            type="span"
            label={<FormattedMessage {...DELIVERY_NOTE_SHARE_MESSAGES.hint} />}
          />
          <Button
            type="button"
            className="btn-small btn-outline"
            onClick={onClose}
            id="delivery-note-template-share-cancel"
          >
            <FormattedMessage {...DELIVERY_NOTE_SHARE_ACTIONS.cancel} />
          </Button>
          <Button
            type="button"
            color="btn-small gray-700"
            onClick={submit}
            disabled={shareStatus === QUERY_STATUS.LOADING}
            id="delivery-note-template-share-submit"
          >
            <FormattedMessage {...DELIVERY_NOTE_SHARE_ACTIONS.submit} />
          </Button>
        </div>
      </div>

      <ValidationModal
        open={shareStatus !== QUERY_STATUS.INITIAL}
        status={shareStatus}
        onClose={closeValidation}
        messages={{
          values: { name: template.name, colleagues: sharedNames },
          pendingTitleId: DELIVERY_NOTE_SHARE_MESSAGES.pendingTitle.id,
          pendingTitleDefault:
            DELIVERY_NOTE_SHARE_MESSAGES.pendingTitle.defaultMessage,
          successTitleId: DELIVERY_NOTE_SHARE_MESSAGES.successTitle.id,
          successTitleDefault:
            DELIVERY_NOTE_SHARE_MESSAGES.successTitle.defaultMessage,
          successMessageId: DELIVERY_NOTE_SHARE_MESSAGES.successMessage.id,
          successMessageDefault:
            DELIVERY_NOTE_SHARE_MESSAGES.successMessage.defaultMessage,
          errorTitleId: DELIVERY_NOTE_SHARE_MESSAGES.errorTitle.id,
          errorTitleDefault:
            DELIVERY_NOTE_SHARE_MESSAGES.errorTitle.defaultMessage,
          ...errorMessages(),
        }}
      />
    </>,
    document.getElementById("portal")
  );
};

DeliveryNoteTemplateShareModal.propTypes = {
  template: PropTypes.object,
  onSearchColleagues: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default DeliveryNoteTemplateShareModal;
